<template>
  <Modal
    header="BetaScore Usage"
    class="calico-usage-dialog"
  >
    <template #content>
      <div class="usage-section">
        <div
          v-if="isLoading"
          class="usage-skeleton"
        >
          <div class="box" />
          <div class="box" />
        </div>

        <div
          v-else-if="errorMessage"
          class="usage-error body--small"
        >
          {{ errorMessage }}
        </div>

        <div
          v-else-if="!hasUsageData"
          class="usage-fallback"
        >
          <div class="heading--xsmall">
            No usage available
          </div>
          <div class="body--small">
            This feature isn’t available for your account or organization at this time.
          </div>
        </div>

        <template v-else>
          <CalicoUsageLimitBox
            :limit="limit"
            :remaining="remaining"
            :viewed="viewed"
          />
          <CalicoUsageResets :reset-date="usage?.cycle?.endExclusive" />
        </template>
      </div>
    </template>

    <template #footer>
      <button
        v-if="hasUsageData"
        class="button-utility"
        @click="upgrade"
      >
        Upgrade Subscription
      </button>
      <button
        class="button-secondary"
        @click="$emit('close')"
      >
        Close
      </button>
    </template>
  </Modal>
</template>

<script>
import { toRef } from "vue";
import Modal from "@/components/template/modal/index.vue";
import CalicoUsageLimitBox from "@/components/calico/usageLimitBox/index.vue";
import CalicoUsageResets from "@/components/calico/usageResets/index.vue";
import { deriveUsageMetrics, upgrade } from "@/composables/auth/useUsage";

export default {
  name: "CalicoUsageDialog",
  components: { 
    Modal, 
    CalicoUsageLimitBox, 
    CalicoUsageResets 
  },
  props: {
    usage: { 
      type: Object, 
      default: null 
    },
    loading: { 
      type: Boolean, 
      default: false 
    },
    errorMessage: { 
      type: String, 
      default: "" 
    }
  },
  setup(props) {
    const usageRef = toRef(props, "usage");
    const loadingRef = toRef(props, "loading");
    const { isLoading, hasUsageData, limit, remaining, viewed } = deriveUsageMetrics(usageRef, loadingRef);
    
    return { 
      isLoading, 
      hasUsageData, 
      limit, 
      remaining, 
      viewed, 
      upgrade 
    };
  }
};
</script>

<style lang="scss">
.calico-usage-dialog {
  .content {
    @include inset-space($space-large);
    @include stack-space($space-large);
    background: $color-background-primary;
    color: $color-text-primary;
  }

  .usage-section {
    @include stack-space($space-medium);
  }

  .usage-box {
    @include inset-space($space-medium);
    @include stack-space($space-medium);
    background: $color-background-secondary;
    border-radius: $radius-small;
    gap: $space-small;
    text-align: center;

    .usage-value {
      margin: $space-small;

      span {
        color: $color-text-primary;
      }

      .has-value {
        color: $color-lapis;
      }

      .is-zero {
        color: $color-text-error;
      }
    }

    .usage-reset {
      color: $color-text-primary;
    }

    .viewed {
      color: $color-text-secondary;
    }
  }

  .usage-fallback {
    @include inset-space($space-large);
    @include stack-space($space-small);
    background: $color-background-secondary;
    border-radius: $radius-small;
    text-align: center;
  }

  .usage-error {
    color: $color-text-error;
    text-align: center;
  }

  .usage-skeleton {
    @include stack-space($space-large);

    .box {
      height: 84px;
      border-radius: $radius-small;
      background: $color-background-secondary;
    }
  }
}
</style>
